/* eslint-disable @typescript-eslint/no-use-before-define,max-classes-per-file */
import { lAsync } from 'app/blocks/common/codes';
import Alerts from 'app/blocks/common/spinner';
import { Deferred, getQueryParam } from 'app/blocks/common/utils';
import WindowMessages from 'app/blocks/common/window-messages';
import * as middlewareOrcid from 'app/blocks/middleware/orcid';

class OrcidConnector {
    constructor() {
        this.handler = new OrcidWindowHandler();
    }

    async connect(skipAccessDeniedError) {
        await this.openModal();
        const orcidCode = new Deferred();

        const windowWatcher = this.checkClosedWindow();
        const process = this.processIntegration.bind(this, orcidCode, windowWatcher, skipAccessDeniedError);
        const event = WindowMessages.addOrcidStorageListener(process);

        const result = await orcidCode.promise;
        WindowMessages.removeOrcidStorageListener(event);
        return result;
    }

    async openModal() {
        if (window.orcidWindow) {
            return;
        }

        window.orcidWindow = true;

        try {
            await this.openOrcidWindow();
        } finally {
            window.orcidWindow = false;
        }
    }

    async openOrcidWindow() {
        window.orcidWindow = window.open('', '_blank');
        if (window.orcidWindow) {
            window.orcidWindow.document.write('Loading...');

            const orcidUrl = await middlewareOrcid.getOrcidURL();
            WindowMessages.setOrcIdParentMessageKey();
            window.orcidWindow.location.href = orcidUrl;
        } else {
            throw new Error(await lAsync('ERROR.POPUP_BLOCKED'));
        }
    }

    async processIntegration(getOrcidCode, windowWatcher, skipAccessDeniedError, options) {
        try {
            Alerts.renderSpinner();
            await this.handler.processIntegration(options, getOrcidCode, windowWatcher, skipAccessDeniedError);
        } finally {
            window.orcidWindow = undefined;
            Alerts.removeSpinner();
        }
    }

    checkClosedWindow() {
        return this.handler.checkClosedWindow(window.orcidWindow);
    }
}

class OrcidWindowHandler {
    async processIntegration(options, getOrcidCode, windowWatcher, skipAccessDeniedError) {
        if (OrcidWindowMethods.isAccessDeniedHref(options)) {
            getOrcidCode.reject(skipAccessDeniedError ? undefined : await this.accessDeniedError());
        } else {
            const orcidCode = OrcidWindowMethods.getOrcidCodeFromHref(options);
            if (orcidCode) {
                getOrcidCode.resolve([orcidCode, options]);
                await windowWatcher.resolve();
                clearInterval(windowWatcher.checkWindowIsClosed);
            } else {
                getOrcidCode.reject(this.noOrcidTokenError());
                clearInterval(windowWatcher.checkWindowIsClosed);
                await windowWatcher.resolve();
            }
        }
    }

    checkClosedWindow(orcidWindow) {
        // eslint-disable-next-line no-use-before-define
        const windowIsClosedWatcher = new Deferred();

        const success = function () {
            clearInterval(this.interval);
        };

        const fail = async function () {
            clearInterval(this.interval);
            this.reject(new Error(await lAsync('ERROR.ORCID_WINDOW_WAS_CLOSED')));
        };
        const onSuccess = success.bind(windowIsClosedWatcher);
        const onFail = fail.bind(windowIsClosedWatcher);

        windowIsClosedWatcher.interval = this.checkWindowIsClosed(orcidWindow, onSuccess, onFail);
        return windowIsClosedWatcher;
    }

    checkWindowIsClosed(orcidWindow, onSuccess, onFail) {
        return setInterval(async () => {
            if (orcidWindow && orcidWindow.closed) {
                // don't fail once window closing detected - will wait one more tick,
                // probably we receive message a bit later
                if (!this.alreadyClosed) {
                    this.alreadyClosed = true;
                    onSuccess();
                } else {
                    await onFail();
                }
            }
        }, 1000);
    }

    async accessDeniedError() {
        return new Error(await lAsync('ERROR.ORCID_ACCESS_DENIED_ERROR'));
    }

    async noOrcidTokenError() {
        return new Error(await lAsync('ERROR.ORCID_FETCH_DETAILS_ERROR'));
    }
}

class OrcidWindowMethods {
    static isAccessDeniedHref(options) {
        return /\?(error=access_denied)&(error_description=).*/.test(options.redirectUrlHref);
    }

    static hasOrcidCodeInHref(options) {
        return /[?&]code=/.test(options.redirectUrlHref);
    }

    static getOrcidCodeFromHref(options) {
        return OrcidWindowMethods.hasOrcidCodeInHref(options) && getQueryParam(options.redirectUrlHref).code;
    }
}

const getOrDefaultRedirectOptions = origin => {
    const options = { ...origin };
    options.redirectURL = options.redirectURL || window.location.hash;
    options.redirectUrlHref = options.redirectUrlHref || window.location.href;
    return options;
};

const handleOrcidRedirect = opts => {
    const options = getOrDefaultRedirectOptions(opts);
    const isInNewWindow = window.opener && window.opener !== window;
    options.shouldInvokeCallback = true;
    WindowMessages.setOrcIdParentMessage(options);
    if (isInNewWindow) {
        setTimeout(() => window.close(), 500);
    }
};

export { handleOrcidRedirect };
export default OrcidConnector;
